import { apiFetch, withAuth } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * Fetches all zones, optionally filtered by location.
 * @param {object} [accessToken]
 * @param {number} [locationId]
 * @returns {Promise<Array>}
 */
export async function fetchZones(accessToken, locationId) {
  const params = locationId ? `?location_id=${locationId}` : "";
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/zones${params}`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  return await resp.json();
}

/**
 * Fetches a single zone by id.
 * @param {object} accessToken
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function fetchZone(accessToken, id) {
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/zones/${id}`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  return await resp.json();
}

/**
 * Creates a new zone.
 * @param {object} accessToken
 * @param {{ locationId: number, number: number }} req
 * @returns {Promise<object>}
 */
export async function createZone(accessToken, req) {
  const opts = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/zones`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );
  if (resp.status !== 201) throw await resp.text();
  return await resp.json();
}

/**
 * Updates a zone.
 * @param {object} accessToken
 * @param {number} id
 * @param {{ number: number }} req
 * @returns {Promise<object>}
 */
export async function updateZone(accessToken, id, req) {
  const opts = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/zones/${id}`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );
  if (resp.status !== 200) throw await resp.text();
  return await resp.json();
}

/**
 * Soft-deletes a zone.
 * @param {object} accessToken
 * @param {number} id
 */
export async function deleteZone(accessToken, id) {
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/zones/${id}`,
    accessToken
      ? withAuth(accessToken, { method: "DELETE" })
      : { method: "DELETE" },
  );
  if (resp.status !== 200) throw await resp.text();
}

/**
 * Fetches all computers, optionally filtered by zone.
 * @param {object} [accessToken]
 * @param {number} [zoneId]
 * @returns {Promise<Array>}
 */
export async function fetchComputers(accessToken, zoneId) {
  const params = zoneId ? `?zone_id=${zoneId}` : "";
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers${params}`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  return await resp.json();
}

/**
 * Fetches a single computer by id.
 * @param {object} accessToken
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function fetchComputer(accessToken, id) {
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers/${id}`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  return await resp.json();
}

/**
 * Fetches default values for the PC creation form.
 * Returns the current user's location, latest zone, and next PC number.
 * When `params.locationId` or `params.zoneId` is given, defaults are
 * computed for that specific location/zone instead.
 * @param {object} [accessToken]
 * @param {{ locationId?: number|string, zoneId?: number|string }} [params]
 * @returns {Promise<{locationId?: number, zoneId?: number, number?: number, redirect?: boolean}>}
 */
export async function fetchComputerDefaults(accessToken, params = {}) {
  const search = new URLSearchParams();
  if (params.locationId != null) search.set("location_id", params.locationId);
  if (params.zoneId != null) search.set("zone_id", params.zoneId);
  const qs = search.toString();
  const opts = accessToken ? withAuth(accessToken) : {};
  // The backend signals "no zones for this location" with a 303 redirect to
  // GET /zones. Without `redirect: "manual"` the browser follows it and we
  // never see the 303 — instead we'd parse the zones array as defaults.
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers/defaults${qs ? `?${qs}` : ""}`,
    { ...opts, redirect: "manual" },
  );
  if (resp.status === 303) {
    return { redirect: true };
  }
  if (resp.status !== 200) throw await resp.text();
  return await resp.json();
}

/**
 * Fetches localized computer status labels from the backend.
 * Returns a map of ComputerStatus enum keys to their translations
 * based on the Accept-Language header.
 * @param {object} [accessToken]
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchComputerStatuses(accessToken) {
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers/statuses`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  if (resp.status !== 200) throw await resp.text();
  return await resp.json();
}

/**
 * Creates a new computer. Status is not part of the create request —
 * new computers always start as "offline".
 * @param {object} accessToken
 * @param {{ zoneId: number, number: number, apiKey: string }} req
 * @returns {Promise<object>}
 */
export async function createComputer(accessToken, req) {
  const opts = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );
  if (resp.status !== 201) throw await resp.text();
  return await resp.json();
}

/**
 * Updates a computer.
 * @param {object} accessToken
 * @param {number} id
 * @param {{ zoneId?: number, number?: number, status?: string }} req
 * @returns {Promise<object>}
 */
export async function updateComputer(accessToken, id, req) {
  const opts = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers/${id}`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );
  if (resp.status !== 200) throw await resp.text();
  return await resp.json();
}

/**
 * Soft-deletes a computer.
 * @param {object} accessToken
 * @param {number} id
 */
export async function deleteComputer(accessToken, id) {
  const resp = await apiFetch(
    `${HOST}/api/v1/playtime/computers/${id}`,
    accessToken
      ? withAuth(accessToken, { method: "DELETE" })
      : { method: "DELETE" },
  );
  if (resp.status !== 200) throw await resp.text();
}
